// shared-lib/cabinet-runs/cornerTreatment.js
//
// What a run's corner HOLDS, recorded on the run so a re-solve reproduces it.
//
// A generated kitchen decides each corner once — an L unit, a single-sided
// unit on one leg, or a box left empty under a bridged counter — and the run
// engine decides it again on the first edit, from openings and hard pieces
// alone. Where the two disagree the first edit changes the kitchen: an L
// appears in a box the generator left open, or a void replaces a unit. The
// join used to avoid that by joining only where the generator had built an L
// (#1494) — which left a U whose corner was blind or empty as three runs.
//
// A counter that turns is one run (single-run plan, 2026-09-12). So the join
// turns at every vertex both legs reach, and the generator's decision rides
// along as a corner override `{ cornerIndex, treatment }` that `fillRun`'s
// corner plan honours instead of re-deriving. The treatment is DERIVED from
// what the generator built, never guessed: the unit in the box names it, and
// with no unit the bodies on the two legs say which leg runs to the vertex.
//
// Pure. No I/O.
const { footprintFor } = require('../geometry/objectFootprint');
const { getWallAxes } = require('../kitchen-assembly/wallMath');
const { cornerUnitWallIds } = require('./cornerUnit');
const { isObliqueCorner } = require('./corners');
const { overridesForRun, tierOf } = require('./bayOverrides');
const { KITCHEN_CABINET_DIMENSIONS } = require('../kitchen-assembly/constants');
const { claimsCorner } = require('../kitchen-assembly/cornerProximity');

const IN = 0.0254;
/**
 * A leg "runs to the vertex" when its nearest lower body starts within half
 * a corner box of it. The generator's blind-corner reservation holds the
 * other leg a full cabinet depth (24") off the vertex, and the L's legs a full
 * 36" footprint, so the two populations sit well either side of 12".
 */
const REACH_EPS = 12 * IN;
/** A body whose far edge is within this of the vertex is behind it, not on this leg. */
const BEHIND_EPS = 1e-3;

/**
 * Appliances that END a run when they stand at its corner. The run cannot
 * turn onto the adjoining wall, because what lies in front of the appliance
 * is the clearance it needs, not a corner box — the adjoining wall's cabinets
 * are their own run, starting past that gap (user ruling, 2026-09-13). The
 * generator classifies the same set `appliance-on-A` / `appliance-on-B` and
 * reserves the appliance's depth plus its front clearance on the other wall;
 * a sink reaches this only when it could not slide clear of the corner.
 *
 * The fridge is measured by its BOX, as the generator measures it: the
 * surround's ½" panels stand half an inch nearer the vertex, and a fridge
 * seated exactly one depth out would end the run here and not there.
 */
const RUN_ENDING_APPLIANCE_TYPES = new Set([
  'refrigerator',
  'range',
  'wall-oven',
  'dishwasher',
  'kitchen-sink',
]);

/** Lower-tier bodies that can stand in a corner box. */
const BODY_TYPES = new Set([
  'lower-cabinet',
  'corner-cabinet',
  'corner-fill-cabinet',
  'dishwasher',
  'range',
  'cooktop',
  'refrigerator',
  'fridge-surround',
]);

/**
 * The walls an object stands on, in the order it names them: its
 * `parentWallIds`, or — on cabinets an applied kitchen layout wrote, which
 * carry `cabinetWallRefs` INSTEAD (`cabinetLayout/fromPhaseB` deletes the
 * other) — every ref's id, a peninsula's virtual wall included. An L corner
 * unit's leg A is the first, leg B the second, either way.
 */
function wallIdsOf(obj) {
  if (obj?.parentWallIds?.length) return obj.parentWallIds;
  return (obj?.cabinetWallRefs || []).map((r) => r?.id).filter(Boolean);
}

/** [min, max] wall params an object's rotated footprint covers, or null. */
function spanOnWall(obj, wall) {
  const c = obj?.center;
  const w = obj?.dimensions?.x;
  const d = obj?.dimensions?.z;
  const axes = getWallAxes(wall);
  if (!c || !(w > 0) || !(d > 0) || !axes) return null;
  const pts = footprintFor({ x: c.x, z: c.z }, w, d, obj.rotation?.y || 0);
  let min = Infinity;
  let max = -Infinity;
  for (const p of pts) {
    const u =
      (p.x - axes.start.x) * axes.wallDir.x +
      (p.z - axes.start.z) * axes.wallDir.z;
    if (u < min) min = u;
    if (u > max) max = u;
  }
  return Number.isFinite(min) ? { min, max } : null;
}

/**
 * How far the nearest lower body on `seg` stands from the vertex at its
 * `end` ('end' | 'start'), along the leg. 0 when a body straddles the vertex,
 * Infinity when the leg carries none.
 */
/**
 * A lower BODY of a run — a base cabinet, corner unit or appliance box. Not a
 * blank panel (a cover, an inch deep), a countertop or an upper.
 */
const isLowerBody = (o) => BODY_TYPES.has(o?.type) && !o.dimensions?.isUpper;
const isCountertop = (o) => o?.type === 'countertop';

/**
 * Whether an object's footprint lies in the band a wall's counter occupies:
 * every corner within the counter depth of the wall line, on the room side.
 * A countertop carries no wall refs, so this is how it is attributed to a
 * wall; `COUNTER_BAND` is the cabinet depth plus the counter's overhang.
 */
const COUNTER_BAND = KITCHEN_CABINET_DIMENSIONS.depth + 3 * IN;
function standsOnWall(obj, wall) {
  const c = obj?.center;
  const w = obj?.dimensions?.x;
  const d = obj?.dimensions?.z;
  const axes = getWallAxes(wall);
  if (!c || !(w > 0) || !(d > 0) || !axes) return false;
  const pts = footprintFor({ x: c.x, z: c.z }, w, d, obj.rotation?.y || 0);
  return pts.every((p) => {
    const off =
      (p.x - axes.start.x) * axes.wallNormal.x +
      (p.z - axes.start.z) * axes.wallNormal.z;
    return off > -BEHIND_EPS && off < COUNTER_BAND;
  });
}

function nearestBodyToVertex(seg, end, objects) {
  return nearestToVertex(seg, end, objects, isLowerBody, (o, wall) =>
    wallIdsOf(o).includes(wall.id)
  );
}

function nearestToVertex(seg, end, objects, accept, onWall) {
  const wall = seg?.wall;
  if (!wall) return Infinity;
  const vertex = end === 'end' ? seg.endParam : seg.startParam;
  const other = end === 'end' ? seg.startParam : seg.endParam;
  const away = Math.sign(other - vertex) || 1;
  let best = Infinity;
  for (const o of objects || []) {
    if (!accept(o)) continue;
    if (!onWall(o, wall)) continue;
    const span = spanOnWall(o, wall);
    if (!span) continue;
    // A body BEHIND the vertex is the far side of a junction — the stretch
    // past a peninsula's root on the wall it springs from — and not this
    // leg's at all. Reading it as "at the vertex" extended the wall leg
    // through an arm's blind box on the first edit (2026-09-13).
    if (
      away > 0
        ? span.max <= vertex + BEHIND_EPS
        : span.min >= vertex - BEHIND_EPS
    )
      continue;
    const d = away > 0 ? span.min - vertex : vertex - span.max;
    best = Math.min(best, Math.max(0, d));
  }
  return best;
}

/**
 * True when an appliance stands at the vertex on this leg, so the run ends
 * here rather than turning.
 *
 * How near the vertex its body must stand: within
 * one cabinet depth, because that is where the OTHER leg's cabinets would be —
 * an appliance whose body reaches into that strip has the other leg's
 * cabinets in front of its door, and one seated exactly a depth out touches
 * the strip's edge and reaches none of it (the common placement: slot
 * enumeration seats appliances flush against the corner reservation). The
 * generator (`analyzeWallAt`) and the scorer (`counterSpace`) ask the same
 * question through the same predicate, `cornerProximity.claimsCorner`, whose
 * epsilon absorbs the last bit of a double on a flush seat; they must agree or
 * the first edit splits a run the generator joined, or joins one it ended.
 */
function applianceEndsRunAt(seg, end, objects) {
  const wall = seg?.wall;
  if (!wall) return false;
  const vertex = end === 'end' ? seg.endParam : seg.startParam;
  const other = end === 'end' ? seg.startParam : seg.endParam;
  const away = Math.sign(other - vertex) || 1;
  for (const o of objects || []) {
    if (!RUN_ENDING_APPLIANCE_TYPES.has(o?.type)) continue;
    if (!wallIdsOf(o).includes(wall.id)) continue;
    const span = spanOnWall(o, wall);
    if (!span) continue;
    if (
      away > 0
        ? span.max <= vertex + BEHIND_EPS
        : span.min >= vertex - BEHIND_EPS
    )
      continue;
    const d = away > 0 ? span.min - vertex : vertex - span.max;
    if (claimsCorner(Math.max(0, d))) return true;
  }
  return false;
}

/**
 * The join predicate for a pair of resolved segments meeting at a vertex:
 * false when an appliance stands at the corner on either leg.
 *
 * Not at an oblique joint. There the legs stop on the bisector whatever
 * stands on them and the wedge is a kite (`corners.js`), which is how the
 * generator treats an appliance at such a corner too — `cornerFillStops` +
 * `maybeEmitObtuseKite`, never `appliance-on-*`'s clearance strip. Refusing
 * here split 213 of the corpus's 623 kite corners into two runs on the first
 * edit (2026-09-13).
 */
function applianceSeparatesLegs(segA, segB, objects) {
  if (isObliqueCorner(segA, segB)) return false;
  return (
    applianceEndsRunAt(segA, 'end', objects) ||
    applianceEndsRunAt(segB, 'start', objects)
  );
}

/** The lower corner unit standing on BOTH legs' walls, or null. */
function cornerUnitAt(segA, segB, objects) {
  const want = [segA?.wall?.id, segB?.wall?.id];
  if (!want[0] || !want[1]) return null;
  for (const o of objects || []) {
    if (o?.type !== 'corner-cabinet' || o.dimensions?.isUpper) continue;
    const ids = cornerUnitWallIds(o);
    if (want.every((id) => ids.includes(id))) return o;
  }
  return null;
}

/**
 * The treatment the generator gave the corner between two adjacent segments
 * of a resolved run (each carries `.wall`):
 *   { mode: 'cabinet', length? }                           — an L unit; `length`
 *       (metres) is its leg, so a line's 33" L is rebuilt at 33". A record
 *       without one is the 36" every L was before a line could sell another
 *   { mode: 'blind', extend: 'A'|'B', singleSided: bool, length?, blank? }
 *       — one leg runs to the vertex; `singleSided` says whether a
 *       single-sided unit stands in the box, and `length` (metres) is the
 *       unit the generator built, so the engine builds THAT unit rather than
 *       re-measuring the leg's free run and declining one under 36". `blank`
 *       is the box a true blind corner reserves and leaves EMPTY when no unit
 *       fits (user ruling 2026-09-11): the extending leg's cabinets stop a
 *       depth short like the other leg's, and only its countertop spans the
 *       box — which is how the objects give it away
 *   { mode: 'empty' }                                      — neither leg does
 *   { mode: 'oblique', kite: bool }                        — a joint that is
 *       not a right angle; the engine computes the legs' stops from the
 *       geometry and `kite` says whether the wedge was filled
 */
function cornerTreatmentFromObjects(segA, segB, objects) {
  if (isObliqueCorner(segA, segB)) {
    const want = [segA?.wall?.id, segB?.wall?.id];
    const kite = (objects || []).some(
      (o) =>
        o?.type === 'corner-fill-cabinet' &&
        want.every((id) => wallIdsOf(o).includes(id))
    );
    return { mode: 'oblique', kite };
  }
  const unit = cornerUnitAt(segA, segB, objects);
  if (unit) {
    if (unit.dimensions?.cornerVariant !== 'single-sided') {
      const leg = unit.dimensions?.x;
      return { mode: 'cabinet', ...(leg > 0 ? { length: leg } : {}) };
    }
    // A single-sided unit's OWN wall is its first ref (the face); its end
    // stands against the other. The leg it stands on is the one that extends.
    const own = cornerUnitWallIds(unit)[0];
    const length = unit.dimensions?.x;
    return {
      mode: 'blind',
      extend: own === segB?.wall?.id ? 'B' : 'A',
      singleSided: true,
      ...(length > 0 ? { length } : {}),
    };
  }
  const dA = nearestBodyToVertex(segA, 'end', objects);
  const dB = nearestBodyToVertex(segB, 'start', objects);
  const reachA = dA < REACH_EPS;
  const reachB = dB < REACH_EPS;
  if (reachA || reachB) {
    // Both reaching is the two-fills-in-one-box defect the generator no
    // longer produces; the nearer leg is the one that ran to the vertex.
    const extend = reachA && (!reachB || dA <= dB) ? 'A' : 'B';
    return { mode: 'blind', extend, singleSided: false };
  }
  const cA = nearestToVertex(segA, 'end', objects, isCountertop, standsOnWall);
  const cB = nearestToVertex(
    segB,
    'start',
    objects,
    isCountertop,
    standsOnWall
  );
  if (cA < REACH_EPS || cB < REACH_EPS) {
    const extend = cA < REACH_EPS && (cB >= REACH_EPS || cA <= cB) ? 'A' : 'B';
    return { mode: 'blind', extend, singleSided: false, blank: true };
  }
  return { mode: 'empty' };
}

const treatmentOverrideId = (cornerIndex) => `corner_treatment_${cornerIndex}`;

/**
 * The resolved run with a treatment override recorded for every interior
 * vertex, from the generator's objects. An existing corner override at that
 * index (an appearance edit) gains the field; nothing else is touched.
 */
/**
 * The generator's recorded decision for the corner between two segments, in
 * the run's own A/B orientation, or null. `extendWallId` names the wall whose
 * run reaches the vertex; it becomes `extend: 'A'|'B'` for this traversal.
 */
function recordedTreatmentFor(segA, segB, recorded) {
  const a = segA?.wall?.id;
  const b = segB?.wall?.id;
  if (!a || !b) return null;
  for (const d of recorded || []) {
    if (!d?.treatment) continue;
    if (!((d.a === a && d.b === b) || (d.a === b && d.b === a))) continue;
    const { extendWallId, ...t } = d.treatment;
    if (t.mode === 'blind') {
      return { ...t, extend: extendWallId === a ? 'A' : 'B' };
    }
    return t;
  }
  return null;
}

function withCornerTreatments(run, objects, recorded = null) {
  const segments = run?.segments || [];
  if (segments.length < 2) return run;
  const overrides = [...overridesForRun(run)];
  for (let i = 0; i < segments.length - 1; i += 1) {
    const treatment =
      recordedTreatmentFor(segments[i], segments[i + 1], recorded) ||
      cornerTreatmentFromObjects(segments[i], segments[i + 1], objects);
    const at = overrides.findIndex(
      (o) => o?.cornerIndex === i && tierOf(o) === 'lower'
    );
    if (at >= 0) overrides[at] = { ...overrides[at], treatment };
    else
      overrides.push({ id: treatmentOverrideId(i), cornerIndex: i, treatment });
  }
  const { pins, ...rest } = run;
  return { ...rest, bayOverrides: overrides };
}

/** The recorded treatment for corner `cornerIndex` of `run`, or null. */
function cornerTreatmentOverride(run, cornerIndex) {
  if (cornerIndex == null) return null;
  for (const o of overridesForRun(run)) {
    if (o?.cornerIndex === cornerIndex && tierOf(o) === 'lower' && o.treatment)
      return o.treatment;
  }
  return null;
}

/**
 * The corner plan a recorded treatment dictates, in `cornerPlan`'s shape.
 * `depth` is the run's corner box (one cabinet depth); `footprint` the L's leg
 * when the treatment does not name one (`length`).
 */
function planFromTreatment(treatment, depth, footprint) {
  // An oblique corner's stops come from the geometry; `fillRun` handles it
  // before it asks for a plan. Reaching here with one means the record and
  // the geometry disagree — the geometry wins, so nothing is trimmed here.
  if (treatment?.mode === 'oblique') return null;
  if (treatment?.mode === 'cabinet') {
    const leg = treatment.length > 0 ? treatment.length : footprint;
    return { mode: 'cabinet', length: leg, trimA: leg, trimB: leg };
  }
  if (treatment?.mode === 'blind') {
    const extend = treatment.extend === 'B' ? 'B' : 'A';
    // A blank box: the extending leg's cabinets stop a depth short too.
    const trimX = treatment.blank ? depth : 0;
    return {
      mode: 'blind',
      extend,
      trimA: extend === 'A' ? trimX : depth,
      trimB: extend === 'B' ? trimX : depth,
    };
  }
  return { mode: 'empty', trimA: depth, trimB: depth };
}

module.exports = {
  REACH_EPS,
  RUN_ENDING_APPLIANCE_TYPES,
  applianceEndsRunAt,
  applianceSeparatesLegs,
  cornerTreatmentFromObjects,
  withCornerTreatments,
  recordedTreatmentFor,
  cornerTreatmentOverride,
  planFromTreatment,
  nearestBodyToVertex,
  isLowerBody,
  spanOnWall,
  wallIdsOf,
};
